/**
 * Check rows (DESIGN §3): a round target, the label with its one-line why, and the due time. A one-tap checklist item
 * completes on the target with the check draw; a log opens its form.
 */
import { useState } from "react";
import { useKeyedState } from "../lib/useKeyedState.ts";
import { Link } from "react-router-dom";
import { placeState, getTemplate, isLate, checkAffordance, checkBasisLine, inputKindForExpected, photoWords, type ExpectedCheck, type RoomClearCover } from "@logwave/core";
import { guideFor, guidePath, whyFor } from "../lib/meaning.ts";
import { afterCheckDraw } from "../lib/motion.ts";
import { isoToLocalTime } from "../lib/time.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon } from "./Icons.tsx";
import { Button, CheckTarget, LinkButton, Pill, Skeleton, useToast } from "./ui.tsx";
import { checkActionLabel } from "../lib/labels.ts";
import { isQuickPestLook } from "../lib/pestLook.ts";
import { lazyInline } from "../lib/lazy.ts";
const PestLookRow = lazyInline(() => import("./Pest.tsx"), "PestLookRow", <Skeleton rows={1} />);
import type { CheckoffDone } from "../api.ts";
import { NotDoneButton } from "./Checkoff.tsx";
import { Badge, ItemCardDialog, taskKind, useTaskDone } from "./ItemCard.tsx";

export type Expected = ExpectedCheck & { satisfied: boolean; done?: CheckoffDone; roomClear?: RoomClearCover };

export function dueLabel(e: Expected, tz: string, nowIso = new Date().toISOString()) {
  if (e.satisfied) return <Pill tone="go">Done</Pill>;
  // Neutral wording: "Was due", never "Late" or "Missed" (PEOPLE_AND_NEGATION: no blame-shaped records).
  // Before the first full day nothing is late: it's where you start (core schedule.ts firstDay).
  if (e.firstDay) return <Pill tone="line" icon="clock">To start</Pill>;
  if (isLate(e, nowIso)) return <Pill tone="warn" icon="clock">Was due {isoToLocalTime(e.dueAt, tz)}</Pill>;
  return <Pill tone="line" icon="clock">{isoToLocalTime(e.dueAt, tz)}</Pill>;
}

type RowProps = { e: Expected; onOpen?: (e: Expected) => void; onDone?: (e: Expected) => void; showWhy?: boolean; primary?: boolean };

/** A check row. The quick pest look is one line with its two outcomes (Pest.tsx); every other check is below. */
export function CheckRow(p: RowProps) {
  return isQuickPestLook(p.e) ? <PestLookRow e={p.e} onDone={p.onDone} /> : <PlainCheckRow {...p} />;
}

function PlainCheckRow({ e, onOpen, onDone, showWhy = true, primary }: RowProps) {
  const api = useApi();
  const s = useSession();
  const toast = useToast();
  const task = useTaskDone();
  const [card, setCard] = useState(false);
  // The row follows the server after every reload, so it never says Done while the counter doesn't count it (audit #7). It resets
  // during render and only when the server's answer changes: an effect also ran after the first render and undid a tap made just then.
  const [done, setDone] = useKeyedState(e.satisfied, () => e.satisfied);
  const [busy, setBusy] = useState(false);
  const tz = s.location.timezone;
  const isTask = e.templateId === "task_done" && !!e.taskId;
  // Same gesture, same result (core checkinput.ts): the circle of a one-tap item ticks it; the circle of an item that needs a
  // reading, an answer or a photo opens its card and never ticks it; the name always opens the card.
  const tap = isTask && taskKind(e.label) === "tap";
  const badge = checkAffordance(inputKindForExpected(e)).badge;
  // The check's own one-line "how" (core hint, e.g. which strip and what range) beats the generic requirement summary.
  // A restroom room check's Clear ✓ covers part of this log: say so on the row, with why it isn't the whole log (audit #5).
  // A checklist item's how (core schedule.ts: the owner's words or the starter's) shows even where the why is hidden (the
  // close hand-off): a tick never goes by with nothing to read (crawl fix 3). The circle still ticks; the name opens the card.
  const why = e.roomClear && !done ? e.roomClear.line : showWhy ? e.hint ?? whyFor(e.requirementIds, placeState(s.location, s.org)) : isTask ? e.hint : undefined;
  // Why the check is here at all (core schedule.ts checkBasis): the law's own line where the law names it, else "Recommended. …".
  // Only where the why shows and the check isn't done; a checklist item has none.
  const basis = showWhy && !done ? checkBasisLine(e) : undefined;
  const verb = checkActionLabel(e.templateId, e.label, getTemplate(e.templateId)?.title);
  const guide = guideFor(e.requirementIds, placeState(s.location, s.org));
  const title = isTask ? e.label : getTemplate(e.templateId)?.title;
  const ref = { label: e.label, taskId: e.taskId ?? "" };

  async function complete(extra: { note?: string | null; photoBase64?: string } = {}) {
    setBusy(true);
    setDone(true); // the check draws at once; roll back if the save fails
    const ok = await task.complete(ref, extra, { onUndone: () => { setDone(false); onDone?.(e); } });
    setBusy(false);
    if (!ok) { setDone(false); return false; }
    afterCheckDraw(() => onDone?.(e)); // the list drops this row after the tick has drawn (motion audit M3), not in the middle of it
    return true;
  }

  const main = (
    <span className="check-main">
      <span className="check-label">{e.label}</span>
      {badge && !done && <Badge text={badge} />}
      {why ? <span className="check-hint">{why}</span> : title && title !== e.label ? <span className="check-hint">{title}</span> : null}
      {e.roomClear && !done && <span className="check-hint small">{e.roomClear.why}</span>}
      {basis && <span className="check-hint small" data-testid="check-basis">{basis}</span>}
      {/* Photo proof (core proof.ts): only an asked photo is said on the row; the sheet offers one on every such check. */}
      {e.photo?.asked && !done && <span className="check-hint small" data-testid="check-photo-asked"><Icon name="camera" size={15} /> {photoWords(e.photo)}</span>}
    </span>
  );

  return (
    // [data-next] (peek and resume §4.1): the next control a person most likely taps; the Landed card never covers it.
    <li className={`check-row${done ? " is-done" : ""}`} {...(primary && !done && (tap || !onOpen) ? { "data-next": "" } : {})}>
      {isTask && !done ? (
        <>
          <CheckTarget done={done} busy={busy} label={`Mark done: ${e.label}`} onClick={() => (tap && !e.photo?.asked ? void complete() : setCard(true))} />
          <button type="button" className="check-open" data-testid="check-name" onClick={() => setCard(true)} aria-label={`${e.label}. See how`}>{main}</button>
        </>
      ) : done ? (
        <><CheckTarget done label={e.label} />{main}</>
      ) : onOpen ? (
        <button type="button" className="check-open" onClick={() => onOpen(e)} aria-label={e.label}>
          <CheckTarget done={false} kind="log" label={e.label} />{main}
        </button>
      ) : (
        <Link className="check-open" to={`/do?check=${encodeURIComponent(e.key)}`} aria-label={e.label}>
          <CheckTarget done={false} kind="log" label={e.label} />{main}
        </Link>
      )}
      <span className="check-side">
        {guide && showWhy && <Link to={guidePath(guide)} className="caption" aria-label={`Guide for ${e.label}`}><Icon name="book" size={18} /></Link>}
        {done ? <Pill tone="go">Done</Pill> : dueLabel(e, tz)}
        {done && e.done && (
          <NotDoneButton what={e.label} item={e.done} me={{ userId: s.me.user.id, role: s.role }} tz={tz}
            save={(b) => api.revertCheckoff(s.org.id, "entry", e.done!.id, b)} onDone={() => { setDone(false); onDone?.(e); }} />
        )}
        {primary && !done && !isTask && (onOpen
          ? <Button small icon="chevron" iconEnd data-next onClick={() => onOpen(e)}>{verb}</Button>
          : <LinkButton small icon="chevron" iconEnd to={`/do?check=${encodeURIComponent(e.key)}`}>{verb}</LinkButton>)}
      </span>
      {card && <ItemCardDialog item={{ label: e.label, taskId: e.taskId ?? "", ...(e.hint ? { how: e.hint } : {}) }} {...(e.photo ? { photo: e.photo } : {})} busy={busy} onClose={() => setCard(false)} onDone={async (extra) => { if (await complete(extra)) setCard(false); }} />}
    </li>
  );
}
